import type {
    IAdjustmentRule,
    IConnection,
    IDaylightTransition,
    IMultiConnection,
    ITimeZoneInfo,
} from "./types";
import { clearRuleBoundsMemo, getBaseDeltaForDate } from "./standardOffsetChange";

export interface IDstCalculator {
    initialize: (
        conn?: IConnection,
        errHandler?: (e: Error) => void,
        userTimeZoneId?: string,
        timeZoneInfoWithRules?: ITimeZoneInfo[],
        successCallback?: () => void
    ) => void;
    setTimeZoneInfo: (timeZoneId: string, timeZoneInfo: ITimeZoneInfo) => void;
    hasTimeZoneInfo: (timeZoneId: string) => boolean;
    toLocalDateTime: (
        dateTimeUtc: Date | string,
        timeZoneId?: string | ITimeZoneInfo,
        cacheBrowserTimezone?: boolean
    ) => Date;
    toReverseOffsetDateTime: (
        dateTime: Date,
        timeZoneId?: string | ITimeZoneInfo,
        cacheBrowserTimezone?: boolean
    ) => Date;
    getOffset: (
        dateTimeUtc: Date,
        timeZoneId?: string,
        toLocal?: boolean
    ) => number;
    getUtcOffset: (dateTimeUtc: Date, timeZoneId?: string) => number;
    populateTimeZone: (timeZoneId: string) => Promise<ITimeZoneInfo>;
    addTimeZone: (timeZoneInfo: ITimeZoneInfo) => void;
    requestTimeZone: (timeZoneId: string) => Promise<ITimeZoneInfo>;
    timeSpanParse: (timespan: string) => Date;
    getDefaultTimeZone: () => string;
}

interface IDstTransition {
    firstHalfTransitionDate: Date;
    secondHalfTransitionDate: Date;
    firstHalfTransitionDateForReverse: Date;
    secondHalfTransitionDateForReverse: Date;
    isDaylightSavingTimeSupported: boolean;
    delta: number;
    reverse: boolean;
}

export function getStartOfDay(d: string | Date): Date {
    let date = new Date(typeof d === "string" ? d : d.valueOf());
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
}

export function getEndOfDay(d: string | Date): Date {
    let date = new Date(typeof d === "string" ? d : d.valueOf());
    date.setHours(23);
    date.setMinutes(59);
    date.setSeconds(59);
    date.setMilliseconds(0);
    return date;
}

export const getStartOfMonth = () => {
    let date = new Date();
    date.setDate(1);
    date.setHours(0);
    date.setMinutes(0);
    date.setSeconds(0);
    date.setMilliseconds(0);
    return date;
};

export const getNowDate = () => {
    const dateNow = new Date();
    dateNow.setMinutes(0);
    dateNow.setSeconds(0);
    dateNow.setMilliseconds(0);
    return dateNow.toISOString();
};

const timeSpanParseMemo = new Map<string, number>();

export const timeSpanToMs = function (timestr: string | Date): number {
    const tempStr = typeof timestr === "string" ? timestr : timestr.toString();

    const cached = timeSpanParseMemo.get(tempStr);
    if (cached !== undefined) {
        return cached;
    }

    const isNeg = tempStr.charAt(0) === "-";
    const durationStr = isNeg ? tempStr.slice(1) : tempStr;

    const timeParts = durationStr.split(":");
    const hoursStr = timeParts[0];
    const min = parseInt(timeParts[1], 10);
    const secStr = timeParts[2] || "0.0";

    const secondsParts = secStr.split(".");
    let sec = parseInt(secondsParts[0], 10);

    // Make sure millStr's length is at least 3.
    let mill = 0;
    if (secondsParts.length === 2) {
        let millStr = secondsParts[1].concat("000");
        mill = parseInt(millStr.substring(0, 3), 10) || 0;
    } else {
        mill = 0;
    }

    let days = 0;
    let hours = 0;
    const hoursParts = hoursStr.split(".");
    if (hoursParts.length === 2) {
        days = parseInt(hoursParts[0], 10);
        hours = parseInt(hoursParts[1], 10);
    } else {
        hours = parseInt(hoursStr, 10);
    }

    //These hours are valid UTC not local time. ie. getHours() will be incorrect. use getUTCHours()
    mill =
        (isNeg ? -1 : 1) *
        ((((days * 24 + hours) * 60 + min) * 60 + sec) * 1000 + mill);
    timeSpanParseMemo.set(tempStr, mill);

    return mill;
};

export const timeSpanParse = function (timestr: string | Date): Date {
    const date = new Date();
    date.setTime(timeSpanToMs(timestr));
    return date;
};

const DAY_OF_WEEK_OFFSET = {
    Sunday: 0,
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
};

const transitionDateMemo = new Map<string, number[]>();
export const getTransitionDate = (
    daylightTransition: IDaylightTransition,
    fullYear: number,
    offset: number
) => {
    let cacheKey = `${daylightTransition.day}_${daylightTransition.dayOfWeek}_${
        daylightTransition.month
    }_${daylightTransition.timeOfDay}_${daylightTransition.week}_${String(
        daylightTransition.isFixedDateRule
    )}_${fullYear}_${offset}`;
    let cacheValue = transitionDateMemo.get(cacheKey);
    if (cacheValue?.length === 3) {
        cacheValue[0]++;
        return {
            userTimeZone: new Date(cacheValue[1]),
            browserTimeZone: new Date(cacheValue[2]),
        };
    }

    // Need halp?
    // See "Remarks" here: http://msdn.microsoft.com/en-us/library/system.timezoneinfo.transitiontime.isfixeddaterule.aspx
    // (and also this document here: http://msdn.microsoft.com/en-us/library/system.timezoneinfo.transitiontime.aspx)
    // July 15 doesn't interfere with any DST switchovers
    let baseDate = new Date(fullYear, 6, 15);
    let transitionDate;
    let month = daylightTransition.month - 1;

    baseDate.setUTCFullYear(fullYear);
    baseDate.setUTCMonth(month);
    baseDate.setUTCDate(1);
    baseDate.setUTCHours(0);
    baseDate.setUTCMinutes(0);
    baseDate.setUTCSeconds(0);
    baseDate.setUTCMilliseconds(0);
    if (daylightTransition.isFixedDateRule) {
        baseDate.setUTCDate(daylightTransition.day);
        transitionDate = baseDate;
    } else {
        // Based on first day of the specified week
        // Ie: dayOfWeek is 0 and week is 5, means the 5th Sunday in the specified month

        // dayOfWeekDateChange -> the numeric day of the week when DST changeover occurs (ie. 0 = Sunday, 1 = Monday, etc.)
        let dayOfWeekDateChange =
            DAY_OF_WEEK_OFFSET[
                daylightTransition.dayOfWeek as keyof typeof DAY_OF_WEEK_OFFSET
            ];

        // dayOfWeekDateChange -> the numeric day of the week when the 1st of the month is (ie. 0 = Sunday, 1 = Monday, etc.)
        let dayOfFirstDayInMonth = baseDate.getUTCDay();

        // firstDateInWeek -> the date of the day at the beginning of the week
        //                    ie. if the 1st of the month is a Tuesday, then this value is -1 (because Tuesday is 1, Monday is 0, and Sunday is -1)
        //                        Another way to think of this is if this month is October, we're looking for October -1.
        let firstDateInWeek = baseDate.getUTCDate() - dayOfFirstDayInMonth;

        // weekNumber -> The number of the week to look for the occurrence of the day of week date change date.
        //               All that was done here is to make it zero-based for our calculation as C# gives a value from 1-5.
        let weekNumber = daylightTransition.week - 1;

        if (dayOfWeekDateChange < dayOfFirstDayInMonth) {
            // Use the next Sunday if the beginning of the week where the month starts is in the previous month
            // ie. The first day of the month is on a Tuesday; we don't want Sunday the 29th of the previous month,
            //     we want the next Sunday (the 6th).
            firstDateInWeek += 7;
        }
        transitionDate = baseDate;

        // Change the date to the xth occurrence of the yth day of the week
        transitionDate.setUTCDate(
            firstDateInWeek + 7 * weekNumber + (dayOfWeekDateChange || 0)
        );
        if (weekNumber === 4 && transitionDate.getUTCMonth() === month + 1) {
            // If we're in here, that means there is no 5th occurrence of the day of the week (Sunday, Monday, Tuesday, etc.) in this month,
            // so we simply use the 4th occurrence instead
            transitionDate.setUTCDate(transitionDate.getUTCDate() - 7);
        }
    }

    // Get the time offset when this change will occur
    let tempDate = new Date(daylightTransition.timeOfDay);
    tempDate.setUTCFullYear(1970);

    //DST timestamp
    let utcTimeStampOfDST = transitionDate.getTime() + tempDate.getTime();

    let browserDateOfDST = new Date(
        transitionDate.getTime() + tempDate.getTime()
    );
    let browserTimeZoneOffset = browserDateOfDST.getTimezoneOffset();

    // update cache
    let userTimeZoneMs = utcTimeStampOfDST - offset * 60 * 1000;
    let browserTimeZoneMs =
        utcTimeStampOfDST + browserTimeZoneOffset * 60 * 1000;
    transitionDateMemo.set(cacheKey, [1, userTimeZoneMs, browserTimeZoneMs]);

    return {
        //DST date and time for user Timezone
        userTimeZone: new Date(userTimeZoneMs),

        //DST date and time for browser Timezone
        browserTimeZone: new Date(browserTimeZoneMs),
    };
};

const getDstTransitionForYear = (
    rule: IAdjustmentRule,
    fullYear: number,
    baseOffset: number
): IDstTransition => {
    let deltaInMinutes = timeSpanToMs(rule.daylightDelta) / 1000 / 60,
        startObj = getTransitionDate(
            rule.daylightTransitionStart,
            fullYear,
            baseOffset
        ),
        endObj = getTransitionDate(
            rule.daylightTransitionEnd,
            fullYear,
            baseOffset + deltaInMinutes
        ),
        startTransition = startObj.userTimeZone,
        endTransition = endObj.userTimeZone,
        //see toReverseOffsetDateTime
        startBrowserTransition = startObj.browserTimeZone,
        endBrowserTransition = endObj.browserTimeZone;

    return {
        firstHalfTransitionDate:
            startTransition < endTransition ? startTransition : endTransition,
        secondHalfTransitionDate:
            startTransition < endTransition ? endTransition : startTransition,
        firstHalfTransitionDateForReverse:
            startBrowserTransition < endBrowserTransition
                ? startBrowserTransition
                : endBrowserTransition,
        secondHalfTransitionDateForReverse:
            startBrowserTransition < endBrowserTransition
                ? endBrowserTransition
                : startBrowserTransition,
        isDaylightSavingTimeSupported: true,
        delta: deltaInMinutes,
        reverse:
            rule.daylightTransitionStart.month >
            rule.daylightTransitionEnd.month,
    };
};

// only for Linux DST rules format
const mergeDstRules = (
    currYearRule: IAdjustmentRule,
    prevOrNextYearRule: IAdjustmentRule
): IAdjustmentRule => {
    let isReverseDST =
        timeSpanToMs(currYearRule.daylightDelta) <
        timeSpanToMs(prevOrNextYearRule.daylightDelta);
    let ruleWithDelta = isReverseDST ? prevOrNextYearRule : currYearRule;

    return {
        dateStart: currYearRule.dateStart,
        dateEnd: currYearRule.dateEnd,
        daylightDelta: ruleWithDelta.daylightDelta,
        daylightTransitionEnd: isReverseDST
            ? currYearRule.daylightTransitionStart
            : currYearRule.daylightTransitionEnd,
        daylightTransitionStart: isReverseDST
            ? currYearRule.daylightTransitionEnd
            : currYearRule.daylightTransitionStart,
    };
};

let diff = (a: string, b: string) =>
    Math.abs(new Date(b).valueOf() - new Date(a).valueOf());

const distanceFromDateToTransition = (
    rule: IAdjustmentRule,
    fullYear: number,
    baseOffset: number,
    dateTime: Date
) => {
    let date = dateTime.toISOString();
    let deltaInMinutes = timeSpanToMs(rule.daylightDelta) / 1000 / 60;
    let start = getTransitionDate(
        rule.daylightTransitionStart,
        fullYear,
        baseOffset
    );
    let startDate = start.userTimeZone.toISOString();
    let end = getTransitionDate(
        rule.daylightTransitionEnd,
        fullYear,
        baseOffset + deltaInMinutes
    );
    let endDate = end.userTimeZone.toISOString();
    if (date >= startDate && date <= endDate) {
        return 0;
    }
    return Math.min(diff(startDate, date), diff(endDate, date));
};

const getClosestTransition = (
    transitions: IAdjustmentRule[],
    fullYear: number,
    baseOffset: number,
    dateTime: Date
): IAdjustmentRule | undefined => {
    if (!transitions.length) {
        return undefined;
    }
    if (transitions.length === 1) {
        return transitions[0];
    }
    let closest = transitions[0];
    for (let i = 1; i < transitions.length; i++) {
        if (
            distanceFromDateToTransition(
                transitions[i],
                fullYear,
                baseOffset,
                dateTime
            ) <
            distanceFromDateToTransition(
                closest,
                fullYear,
                baseOffset,
                dateTime
            )
        ) {
            closest = transitions[i];
        }
    }
    return closest;
};

const getDSTTransition = (
    rules: IAdjustmentRule[],
    fullYear: number,
    baseOffset: number,
    dateTime: Date
): IDstTransition => {
    // This year is described with one rule or it might be a Windows OS format
    if (rules.length === 1) {
        return getDstTransitionForYear(rules[0], fullYear, baseOffset);
    }

    // Linux format when all periods are separate IAdjustmentRule
    let sortedRules = rules.sort((r1, r2) =>
        r1.dateStart < r2.dateStart ? -1 : 1
    );
    let thisYearTransitions: IAdjustmentRule[] = sortedRules.filter((rule) => {
        let yearStart = new Date(rule.dateStart).getUTCFullYear();
        let yearEnd = new Date(rule.dateEnd).getUTCFullYear();

        return fullYear === yearStart && fullYear === yearEnd;
    });
    let thisYearTransition = getClosestTransition(
        thisYearTransitions,
        fullYear,
        baseOffset,
        dateTime
    );

    // we need to get it because DST can change within a year and it may start with one shift and ends with another
    let month = dateTime.getUTCMonth();
    let isFirstHalfOfYear = month < 6;
    let ruleNearCurrentYearRules = sortedRules.filter((rule) => {
        if (isFirstHalfOfYear) {
            let yearStart = new Date(rule.dateStart).getUTCFullYear();
            return yearStart < fullYear;
        }

        let yearEnd = new Date(rule.dateEnd).getUTCFullYear();
        return yearEnd > fullYear;
    });

    if (ruleNearCurrentYearRules.length === 0) {
        ruleNearCurrentYearRules = isFirstHalfOfYear
            ? sortedRules.slice(0, 1)
            : sortedRules.slice(-1);
    }

    let ruleNearCurrentYearRule = ruleNearCurrentYearRules[0];

    return getDstTransitionForYear(
        mergeDstRules(
            thisYearTransition || ruleNearCurrentYearRule,
            ruleNearCurrentYearRule
        ),
        fullYear,
        baseOffset
    );
};

export const getDSTOffset = (
    transitions: IAdjustmentRule[],
    fullYear: number,
    baseOffset: number,
    toLocalDate: boolean,
    dateTime: Date
): number => {
    // no DST rules
    if (transitions.length === 0) {
        return 0;
    }

    let transition = getDSTTransition(
        transitions,
        fullYear,
        baseOffset,
        dateTime
    );
    let firstHalf = toLocalDate
        ? transition.firstHalfTransitionDate
        : transition.firstHalfTransitionDateForReverse;
    let secondHalf = toLocalDate
        ? transition.secondHalfTransitionDate
        : transition.secondHalfTransitionDateForReverse;

    //if we want to get user local date we need DST date in user Timezone
    //if we want to get reversed date we need DST date in browser Timezone
    //See BugzID: 156705
    if (
        dateTime.getTime() >= firstHalf.getTime() &&
        dateTime.getTime() < secondHalf.getTime()
    ) {
        if (!transition.reverse) {
            // day light savings time
            return transition.delta;
        }
    } else if (transition.reverse) {
        // day light savings time
        return transition.delta;
    }

    return 0;
};

export function DstCalculatorConstructor(): IDstCalculator {
    let connection: IConnection | undefined,
        cachedBrowserTimeZoneOffset: number | undefined,
        timeZoneOffsetLookup: { [timeZoneId: string]: number } = {},
        errorHandler: (e: Error) => void,
        timeZoneInfoLookup: { [timeZoneId: string]: ITimeZoneInfo } = {},
        timeZoneInfoLoadPromises: Map<string, Promise<ITimeZoneInfo>> = new Map(),
        // For whatever the reason, if the given Timezone id is not valid, we will fall back to Eastern Standard Time.
        // TODO: move all instances of timezone validation to server-side
        defaultTimeZoneId = "America/New_York",
        localUserTimeZoneId: string,
        timeZoneInfoInitialized = false,
        clearYearDstLookup = () => {
            clearRuleBoundsMemo();
            Object.keys(timeZoneInfoLookup).forEach((timezoneId) => {
                if (
                    timeZoneInfoLookup[timezoneId].hasOwnProperty(
                        "yearDstLookup"
                    )
                ) {
                    timeZoneInfoLookup[timezoneId].yearDstLookup = {};
                }
            });
        },
        // Get current timezone offset for browser when there is no DST (on July, 15)
        getBrowserTimezoneOffset = (cacheBrowserTimezone = false) => {
            if (!cacheBrowserTimezone) {
                let year = new Date().getFullYear();
                return new Date(year, 6, 15).getTimezoneOffset();
            }
            if (cachedBrowserTimeZoneOffset === undefined) {
                let year = new Date().getFullYear();
                cachedBrowserTimeZoneOffset = new Date(
                    year,
                    6,
                    15
                ).getTimezoneOffset();
            }
            return cachedBrowserTimeZoneOffset;
        },
        getBrowserTimezoneOffsetForDate = (dateTimeUtc: Date) =>
            -dateTimeUtc.getTimezoneOffset(),
        // Previous local timezone offset. We need it to check if device timezone has changed
        previousTimezoneOffset: number = getBrowserTimezoneOffset(),
        getOffsetRelativeToBrowserTimeZone = (
            dateTimeUtc: Date,
            timeZoneIdObj?: string,
            toLocal?: boolean,
            cacheBrowserTimezone = false
        ) => {
            return getOffset(
                dateTimeUtc,
                getBrowserTimezoneOffsetForDate(dateTimeUtc),
                timeZoneIdObj,
                toLocal,
                cacheBrowserTimezone
            );
        },
        getTimezoneOffset = (timeZoneInfoWithRules: ITimeZoneInfo) => {
            let timeZoneId = timeZoneInfoWithRules.timeZoneId;
            if (!timeZoneOffsetLookup.hasOwnProperty(timeZoneId)) {
                timeZoneOffsetLookup[timeZoneId] =
                    timeSpanToMs(
                        timeZoneInfoWithRules.timeZoneInfo.offsetFromUtc
                    ) /
                    1000 /
                    60;
            }
            return timeZoneOffsetLookup[timeZoneId];
        },
        getOffset = function (
            dateTimeUtc: Date,
            basicOffset: number,
            timeZoneIdObj?: string,
            toLocal?: boolean,
            cacheBrowserTimezone = false
        ) {
            // Calculate offset first.
            let timeZoneId = timeZoneIdObj;
            let fullYear = dateTimeUtc.getUTCFullYear();
            let toLocalDate = toLocal === undefined ? !timeZoneId : toLocal;

            // yearDstLookup looks up DST values, keyed on dst rule name & year
            // Properties of this object are:
            // - firstHalfTransitionDate - the transition date in the first half of the year
            // - secondHalfTransitionDate - the transition date in the latter half of the year
            // - delta - the difference between daylight savings time and normal time in minutes
            // - firstHalfTransitionDate - the transition date in the first half of the year
            // - supportsDaylightSavingTime - some areas support daylight saving time in certain years, and not in certain years
            // - reverse - if it is true, then the timezone is in the Northern hemisphere. Otherwise, it is in the Southern hemisphere. That is, if reverse is true, then any datetime between two transition date is daylight savings time, and outside is not. If reverse is false, then any datetime between two transition date is not daylight savings time, but the outside is

            timeZoneId = timeZoneId || localUserTimeZoneId;

            // TODO: move all instances of timezone validation to server-side
            if (!timeZoneInfoLookup.hasOwnProperty(timeZoneId)) {
                let error =
                    "timeZone missing. Remember to 'changeTimeZone' when new timezone is needed.";
                console &&
                    (typeof console.warn === "function"
                        ? console.warn(error)
                        : console.log(error));
                timeZoneId = defaultTimeZoneId;
            }

            let timeZoneInfoWithRules = timeZoneInfoLookup[timeZoneId];
            let baseOffset = getTimezoneOffset(timeZoneInfoWithRules);
            let currentTimezoneOffset =
                getBrowserTimezoneOffset(cacheBrowserTimezone);

            // If device timezone has been changed then clear yearDstLookup for each timezone
            previousTimezoneOffset !== currentTimezoneOffset &&
                clearYearDstLookup();
            previousTimezoneOffset = currentTimezoneOffset;

            timeZoneInfoWithRules.yearDstLookup =
                timeZoneInfoWithRules.yearDstLookup || {};
            let yearDstLookup = timeZoneInfoWithRules.yearDstLookup;

            let fullYearStr = fullYear.toString();
            let transitions: IAdjustmentRule[] = [];
            if (yearDstLookup.hasOwnProperty(fullYearStr)) {
                transitions = yearDstLookup[fullYearStr];
            } else {
                if (
                    timeZoneInfoWithRules.adjustmentRules &&
                    timeZoneInfoWithRules.adjustmentRules.length > 0
                ) {
                    // ubuntu sends each dst period as a separate adjustment rule
                    let currYearRules =
                        timeZoneInfoWithRules.adjustmentRules.filter((rule) => {
                            let yearStart = new Date(
                                rule.dateStart
                            ).getUTCFullYear();
                            let yearEnd = new Date(
                                rule.dateEnd
                            ).getUTCFullYear();

                            return fullYear >= yearStart && fullYear <= yearEnd;
                        });

                    transitions = currYearRules;
                }

                yearDstLookup[fullYearStr] = transitions;
            }

            let standardOffset =
                baseOffset +
                getBaseDeltaForDate(
                    timeZoneInfoWithRules.adjustmentRules || [],
                    dateTimeUtc,
                    baseOffset,
                    toLocalDate
                );

            let dateTimeOffset =
                standardOffset +
                getDSTOffset(
                    transitions,
                    fullYear,
                    standardOffset,
                    toLocalDate,
                    dateTimeUtc
                );

            // we are trying to use browser local date time to mimic the real local datetime.
            return dateTimeOffset - basicOffset;
        },
        getDaylightSavingRules = function (
            timeZoneIds: string[]
        ): Promise<void> {
            let request: IMultiConnection | undefined;

            for (let i = 0; i < timeZoneIds.length; ++i) {
                if (timeZoneIds[i]) {
                    let params = { timeZoneId: timeZoneIds[i] };
                    if (request) {
                        request = request.andAlso(
                            "GetDaylightSavingRules",
                            params
                        );
                    } else if (connection) {
                        request = connection.call(
                            "GetDaylightSavingRules",
                            params
                        );
                    }
                }
            }

            if (request) {
                const req = request;
                return new Promise((resolve, reject) => {
                    req.beginInvoke(
                        (dstInfos) => {
                            (Array.isArray(dstInfos)
                                ? dstInfos
                                : [dstInfos]
                            ).forEach(addTimeZone);
                            resolve();
                        },
                        (e) => {
                            errorHandler(e);
                            reject(e);
                        }
                    );
                });
            }

            return Promise.resolve(undefined);
        },
        parseTimeZoneId = (timeZoneId?: string | ITimeZoneInfo): string => {
            if (!timeZoneId) {
                return localUserTimeZoneId || defaultTimeZoneId;
            }

            if (typeof timeZoneId !== "string") {
                addTimeZone(timeZoneId);
                return timeZoneId.timeZoneId;
            }

            return timeZoneId;
        },
        addTimeZone = (timeZoneInfo: ITimeZoneInfo) => {
            // Timezone can be removed from the list
            if (
                timeZoneInfo &&
                (timeZoneInfo.timeZoneId || timeZoneInfo.timeZoneInfo)
            ) {
                let timeZoneId =
                    timeZoneInfo.timeZoneId ||
                    timeZoneInfo.timeZoneInfo.id ||
                    "";
                timeZoneInfoLookup[timeZoneId] = timeZoneInfo;
                delete timeZoneOffsetLookup[timeZoneId];
            }
        },
        self: IDstCalculator;

    return (self = {
        /**
         * Finds the day light savings switch dates for this year
         * @return with the year, dst  time and dst end time
         */
        initialize: function (
            conn?: IConnection,
            errHandler?: (e: Error) => void,
            userTimeZoneId?: string,
            timeZoneInfoWithRules?: ITimeZoneInfo[],
            successCallback?: () => void
        ) {
            let success = function () {
                timeZoneInfoInitialized = true;
                localUserTimeZoneId = userTimeZoneId || localUserTimeZoneId;
                successCallback && successCallback();
            };

            connection = conn;
            errorHandler = errHandler || errorHandler;
            if (timeZoneInfoWithRules) {
                timeZoneInfoWithRules.forEach((timeZoneInfo) => {
                    addTimeZone(timeZoneInfo);
                });
                success();
            } else if (userTimeZoneId) {
                let timeZones = [userTimeZoneId];

                // If the user's Timezone isn't the default, add it to the list of Timezones.
                if (userTimeZoneId !== defaultTimeZoneId) {
                    timeZones.push(defaultTimeZoneId);
                }
                getDaylightSavingRules(timeZones).then(success, errHandler);
            }
        },
        setTimeZoneInfo: (timeZoneId: string, timeZoneInfo: ITimeZoneInfo) => {
            timeZoneInfoLookup[timeZoneId] = timeZoneInfo;
            delete timeZoneOffsetLookup[timeZoneId];
        },
        hasTimeZoneInfo: (timeZoneId) =>
            timeZoneInfoLookup.hasOwnProperty(timeZoneId),
        toLocalDateTime: function (
            dateTimeUtc: string | Date,
            timeZone?: string | ITimeZoneInfo,
            cacheBrowserTimezone = false
        ) {
            let timeZoneId = parseTimeZoneId(timeZone);
            let date =
                dateTimeUtc instanceof Date
                    ? dateTimeUtc
                    : new Date(dateTimeUtc);
            let offset = timeZoneInfoInitialized
                ? getOffsetRelativeToBrowserTimeZone(
                      date,
                      timeZoneId,
                      true,
                      cacheBrowserTimezone
                  ) *
                  60 *
                  1000
                : 0;
            let localDate = new Date(date.getTime() + offset);

            //OS can apply local dst for the date on the period of offset
            let browserTimeZoneOffset =
                localDate.getTimezoneOffset() - date.getTimezoneOffset();

            if (browserTimeZoneOffset) {
                localDate = new Date(
                    date.getTime() + offset + browserTimeZoneOffset * 60 * 1000
                );
            }

            return localDate;
        },
        toReverseOffsetDateTime: function (
            dateTime: string | Date,
            timeZone?: string | ITimeZoneInfo,
            cacheBrowserTimezone = false
        ) {
            let timeZoneId = parseTimeZoneId(timeZone);
            let date = dateTime instanceof Date ? dateTime : new Date(dateTime);
            let offset = timeZoneInfoInitialized
                ? getOffsetRelativeToBrowserTimeZone(
                      date,
                      timeZoneId,
                      false,
                      cacheBrowserTimezone
                  ) *
                  60 *
                  1000
                : 0;
            return new Date(date.getTime() - offset);
        },
        getDefaultTimeZone: () => defaultTimeZoneId,
        getOffset: (
            dateTimeUtc: Date,
            timeZoneIdObj?: string,
            toLocal?: boolean
        ) => {
            // this method calculates specific time offeset in compare with browser timezone for the same date
            return getOffsetRelativeToBrowserTimeZone(
                dateTimeUtc,
                timeZoneIdObj,
                toLocal
            );
        },
        getUtcOffset: (
            dateTimeUtc: Date,
            timeZoneIdObj?: string,
            toLocal?: boolean
        ) => {
            return getOffset(dateTimeUtc, 0, timeZoneIdObj, toLocal);
        },
        addTimeZone,
        populateTimeZone: function (
            timeZoneId: string
        ): Promise<ITimeZoneInfo> {
            if (timeZoneInfoLookup[timeZoneId]) {
                return Promise.resolve(timeZoneInfoLookup[timeZoneId]);
            }

            const existing = timeZoneInfoLoadPromises.get(timeZoneId);
            if (existing) {
                return existing;
            }

            const promise = getDaylightSavingRules([timeZoneId])
                .then(() => {
                    const info = timeZoneInfoLookup[timeZoneId];
                    timeZoneInfoLoadPromises.delete(timeZoneId);
                    return info;
                })
                .catch((error) => {
                    timeZoneInfoLoadPromises.delete(timeZoneId);
                    throw error;
                });

            timeZoneInfoLoadPromises.set(timeZoneId, promise);
            return promise;
        },
        requestTimeZone: (timeZoneId: string) => {
            return self.populateTimeZone(timeZoneId).then((info) => {
                if (!info) {
                    throw new Error(`Unknown timezone id: ${timeZoneId}`);
                }

                return info;
            });
        },
        timeSpanParse,
    });
}

